import React, { useRef, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import * as styles from './facility-displays-tab.styles';
import Column from '../../../core/components/column/column.component';
import Row from '../../../core/components/row/row.component';
import { CrossAxisAlignment } from '../../../core/components/flex/flex.component';
import { Center } from '../../../core/components/center/center.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import LabeledInput from '../../../core/components/input/labeled-input.component';
import notify from '../../../core/components/notify/notify.component';
import ConfirmationModal from '../../offices/modals/confirmation-modal.component';
import StyleConstants from '../../../core/styles.constants';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import { useFacilityDisplays } from '../hooks/facility-displays.hook';
import {
  createFacilityDisplay,
  deleteFacilityDisplay,
  pairFacilityDisplay,
  revokeFacilityDisplay,
  updateFacilityDisplay,
} from '../services/facility-display.service';
import type FacilityDisplay from '../models/facility-display';
import RouteConstants from '../../../core/routes.constants';

interface Props {
  readonly facilityId: string;
}

type DisplayStatus = 'pending' | 'live' | 'paused' | 'revoked';

const displayStatus = (display: FacilityDisplay): DisplayStatus => {
  if (display.revokedAt) {
    return 'revoked';
  }
  if (!display.isPaired) {
    return 'pending';
  }
  return display.isPaused ? 'paused' : 'live';
};

// Keep in sync with SUPPORTED_SCREEN_LANGUAGES in facility-display-screen.component.tsx —
// handing out a code the screen doesn't recognise would silently serve the fallback language.
const SCREEN_LINK_LANGUAGES = ['nl', 'en'] as const;
type ScreenLinkLanguage = (typeof SCREEN_LINK_LANGUAGES)[number];

// Built from the shared route constant, not a literal: renaming the route must move the screen, the
// display-id lookup in facility-display-screen.component.tsx and every copied link together.
//
// Offered for any non-revoked display, including one that has never paired and one that isn't shared
// publicly. This URL is not only the public link — it is the screen's own address, and opening it is
// what makes a physical screen show its pairing code in the first place.
const publicScreenUrl = (displayId: string, language: ScreenLinkLanguage): string =>
  `${window.location.origin}/${RouteConstants.PATH_FACILITY_DISPLAY_SCREEN}/${displayId}?lang=${language}`;

// navigator.clipboard.writeText can silently reject (non-secure context, missing permission,
// running inside an iframe without clipboard-write allowed) — select the visible input's text as
// a fallback so the admin can always copy manually via Ctrl/Cmd+C even if the button's own attempt fails.
const copyTextWithFallback = async (text: string, inputElement: HTMLInputElement | null): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the selection-based fallback below
  }

  if (inputElement) {
    inputElement.focus();
    inputElement.select();
    try {
      return document.execCommand('copy');
    } catch {
      return false;
    }
  }

  return false;
};

interface CreateDisplayFormProps {
  readonly facilityId: string;
  readonly onCreated: () => void;
  readonly onCancel: () => void;
}

const CreateDisplayForm = ({ facilityId, onCreated, onCancel }: CreateDisplayFormProps): React.JSX.Element => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | undefined>(undefined);

  const handleCreate = async (): Promise<void> => {
    if (!name.trim() || isSaving) {
      return;
    }

    setErrorKey(undefined);
    setIsSaving(true);
    const created = await createFacilityDisplay({ name: name.trim(), facilityId }, keycloak);
    setIsSaving(false);

    if (!created) {
      setErrorKey('facility-display-create-error');
      return;
    }

    onCreated();
  };

  return (
    <div className={style(styles.sectionCard)}>
      <div className={style(styles.sectionTitle)}>{t('facility-display-create-button')}</div>
      <div className={style(styles.formRow)}>
        <div className={style(styles.formField)}>
          <span className={style(styles.fieldLabel)}>{t('facility-display-name-label').toString()}</span>
          <LabeledInput
            value={name}
            placeholder={t('facility-display-name-placeholder').toString()}
            onChange={setName}
            onEnter={handleCreate}
          />
        </div>
      </div>

      {errorKey && <div className={style(styles.errorText)}>{t(errorKey)}</div>}

      <div className={style(styles.buttonRow)}>
        <div className={style(styles.buttonWrap)}>
          <PrimaryButton loading={isSaving} fullWidth onClick={handleCreate}>
            {t('facility-display-create-button')}
          </PrimaryButton>
        </div>
        <div className={style(styles.buttonWrap)}>
          <PrimaryButton fullWidth disabled={isSaving} onClick={onCancel}>
            {t('cancel')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

interface DisplayRowProps {
  readonly display: FacilityDisplay;
  readonly onChanged: () => void;
}

const DisplayRow = ({ display, onChanged }: DisplayRowProps): React.JSX.Element => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const status = displayStatus(display);
  const [pairingCode, setPairingCode] = useState('');
  const [isPairing, setIsPairing] = useState(false);
  const [pairingErrorKey, setPairingErrorKey] = useState<string | undefined>(undefined);
  const [isBusy, setIsBusy] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [copiedLanguage, setCopiedLanguage] = useState<ScreenLinkLanguage | undefined>(undefined);
  const [showShareConfirm, setShowShareConfirm] = useState(false);
  const linkInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const lastSeenText = display.lastSeenAt
    ? formatDistanceToNow(new Date(display.lastSeenAt), { addSuffix: true })
    : t('facility-display-last-seen-never');
  const copyScreenUrl = async (language: ScreenLinkLanguage): Promise<void> => {
    const copied = await copyTextWithFallback(publicScreenUrl(display.id, language), linkInputRefs.current[language] ?? null);
    if (copied) {
      setCopiedLanguage(language);
      setTimeout(() => setCopiedLanguage(undefined), 2000);
    } else {
      notify(t('facility-display-copy-link-error'), false);
    }
  };

  const applyPublicAccess = async (isPublic: boolean): Promise<void> => {
    setShowShareConfirm(false);
    setIsBusy(true);
    const updated = await updateFacilityDisplay(display.id, { isPublic }, keycloak);
    setIsBusy(false);
    if (!updated) {
      notify(t('facility-display-update-error'), false);
      return;
    }
    onChanged();
  };

  // Turning sharing on asks first (it widens access to anyone holding the link); turning it off
  // is immediate, since that only ever reduces access.
  const togglePublicAccess = (): void => {
    if (display.isPublic) {
      applyPublicAccess(false);
      return;
    }
    setShowShareConfirm(true);
  };

  const handlePair = async (): Promise<void> => {
    if (!pairingCode.trim()) {
      return;
    }

    setPairingErrorKey(undefined);
    setIsPairing(true);
    const result = await pairFacilityDisplay(display.id, pairingCode.trim(), keycloak);
    setIsPairing(false);

    if (!result) {
      setPairingErrorKey('facility-display-pair-error');
      return;
    }

    setPairingCode('');
    onChanged();
  };

  const handleTogglePause = async (): Promise<void> => {
    setIsBusy(true);
    const updated = await updateFacilityDisplay(display.id, { isPaused: !display.isPaused }, keycloak);
    setIsBusy(false);
    if (!updated) {
      notify(t('facility-display-update-error'), false);
      return;
    }
    onChanged();
  };

  const handleRevoke = async (): Promise<void> => {
    setShowRevokeConfirm(false);
    setIsBusy(true);
    const revoked = await revokeFacilityDisplay(display.id, keycloak);
    setIsBusy(false);
    if (!revoked) {
      notify(t('facility-display-revoke-error'), false);
      return;
    }
    onChanged();
  };

  const handleDelete = async (): Promise<void> => {
    setShowDeleteConfirm(false);
    setIsBusy(true);
    const deleted = await deleteFacilityDisplay(display.id, keycloak);
    setIsBusy(false);
    if (!deleted) {
      notify(t('facility-display-delete-error'), false);
      return;
    }
    onChanged();
  };

  return (
    <>
      <tr>
        <td className={style(styles.td)}>{display.name}</td>
        <td className={style(styles.td)}>
          <span className={style(styles.statusBadge(status))}>{t(`facility-display-status-${status}`)}</span>
        </td>
        <td className={style(styles.td)}>{lastSeenText}</td>
        <td className={style(styles.td)}>
          {status !== 'revoked' && (
            <div className={style(styles.actionsCell)}>
              {SCREEN_LINK_LANGUAGES.map((language) => (
                <React.Fragment key={language}>
                  <button
                    type="button"
                    className={style(styles.linkCopyButton)}
                    title={publicScreenUrl(display.id, language)}
                    onClick={(): void => {
                      copyScreenUrl(language);
                    }}
                  >
                    {copiedLanguage === language
                      ? t('facility-display-link-copied')
                      : t(`facility-display-copy-link-${language}`)}
                  </button>
                  {/* Hidden but present in the DOM so the execCommand('copy') fallback has a real
                      selectable input to work with even though the URL isn't shown in the table. */}
                  <input
                    ref={(element): void => {
                      linkInputRefs.current[language] = element;
                    }}
                    className={style(styles.hiddenLinkInput)}
                    type="text"
                    readOnly
                    value={publicScreenUrl(display.id, language)}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                </React.Fragment>
              ))}
            </div>
          )}
        </td>
        <td className={style(styles.td)}>
          {/* A revoked display serves nothing to anyone, so sharing it is meaningless. */}
          <button
            type="button"
            role="switch"
            aria-checked={display.isPublic}
            aria-label={t('facility-display-public-access').toString()}
            disabled={isBusy || status === 'revoked'}
            className={style(styles.publicToggle(display.isPublic, status === 'revoked'))}
            onClick={togglePublicAccess}
          >
            <span className={style(styles.publicToggleKnob(display.isPublic))} />
          </button>
        </td>
        <td className={style(styles.td)}>
          <div className={style(styles.actionsCell)}>
            {/* Shown for any non-revoked display, not just never-paired ones: a replacement or
                reset screen shows a fresh pairing code at any point in a display's life, and
                without an input here there would be no way to enter it. Pairing again simply
                re-binds the display to the new screen and invalidates the previous token. */}
            {status !== 'revoked' && (
              <>
                <input
                  className={style(styles.pairingInput)}
                  type="text"
                  maxLength={6}
                  placeholder={t('facility-display-pairing-code-placeholder').toString()}
                  value={pairingCode}
                  onChange={(event): void => setPairingCode(event.target.value.toUpperCase())}
                />
                <button
                  type="button"
                  className={style(styles.smallButton)}
                  disabled={isPairing || !pairingCode.trim()}
                  onClick={(): void => {
                    handlePair();
                  }}
                >
                  {status === 'pending' ? t('facility-display-pair-button') : t('facility-display-repair-button')}
                </button>
              </>
            )}

            {(status === 'live' || status === 'paused') && (
              <button
                type="button"
                className={style(styles.smallButton)}
                disabled={isBusy}
                onClick={(): void => {
                  handleTogglePause();
                }}
              >
                {status === 'paused' ? t('facility-display-resume-button') : t('facility-display-pause-button')}
              </button>
            )}

            {status !== 'revoked' && (
              <button
                type="button"
                className={style(styles.smallButtonDanger)}
                disabled={isBusy}
                onClick={(): void => setShowRevokeConfirm(true)}
              >
                {t('facility-display-revoke-button')}
              </button>
            )}

            <button
              type="button"
              className={style(styles.smallButtonDanger)}
              disabled={isBusy}
              onClick={(): void => setShowDeleteConfirm(true)}
            >
              {t('facility-display-delete-button')}
            </button>
          </div>
          {pairingErrorKey && <div className={style(styles.errorText)}>{t(pairingErrorKey)}</div>}
        </td>
      </tr>
      <ConfirmationModal
        showModal={showShareConfirm}
        itemName={display.name}
        messageKey="facility-display-share-confirm-message"
        confirmLabelKey="facility-display-share-confirm-button"
        handleAccept={(): void => {
          applyPublicAccess(true);
        }}
        handleClose={(): void => setShowShareConfirm(false)}
      />
      <ConfirmationModal
        showModal={showRevokeConfirm}
        itemName={display.name}
        handleAccept={handleRevoke}
        handleClose={(): void => setShowRevokeConfirm(false)}
      />
      <ConfirmationModal
        showModal={showDeleteConfirm}
        itemName={display.name}
        handleAccept={handleDelete}
        handleClose={(): void => setShowDeleteConfirm(false)}
      />
    </>
  );
};

const FacilityDisplaysTab = ({ facilityId }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const { displays, isLoading, refresh } = useFacilityDisplays(facilityId);
  const [showCreateForm, setShowCreateForm] = useState(false);

  return (
    <Column crossAxisAlignment={CrossAxisAlignment.Stretch} itemSpacing={12}>
      <Row className={style(styles.headerRow)}>
        <span className={style(styles.sectionTitle)}>{t('facility-detail-tab-displays')}</span>
        {!showCreateForm && (
          <div className={style(styles.newButtonWrap)}>
            <PrimaryButton fullWidth onClick={(): void => setShowCreateForm(true)}>
              {t('facility-display-create-button')}
            </PrimaryButton>
          </div>
        )}
      </Row>

      {showCreateForm && (
        <CreateDisplayForm
          facilityId={facilityId}
          onCreated={(): void => {
            setShowCreateForm(false);
            refresh();
          }}
          onCancel={(): void => setShowCreateForm(false)}
        />
      )}

      {isLoading && (
        <Center>
          <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
        </Center>
      )}

      {!isLoading && displays.length === 0 && !showCreateForm && (
        <div className={style(styles.emptyStateWrap)}>
          <div className={style(styles.emptyStateTitle)}>{t('facility-display-empty-state')}</div>
        </div>
      )}

      {!isLoading && displays.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={style(styles.table)}>
            <thead>
              <tr>
                <th className={style(styles.th)}>{t('facility-display-name-label').toString()}</th>
                <th className={style(styles.th)}>{t('facility-display-status-label')}</th>
                <th className={style(styles.th)}>{t('facility-display-last-seen-label')}</th>
                <th className={style(styles.th)}>{t('facility-display-screen-link')}</th>
                <th className={style(styles.th)}>{t('facility-display-public-access')}</th>
                <th className={style(styles.th)}>{t('facility-display-actions-label')}</th>
              </tr>
            </thead>
            <tbody>
              {displays.map((display) => (
                <DisplayRow key={display.id} display={display} onChanged={refresh} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Column>
  );
};

export default FacilityDisplaysTab;
